// Small pieces the transcript's rows share: text folded past a few lines, the
// head of a row that opens, a diff. Moved from today's Code tab
// (code/bits.tsx there).
import { useId, useMemo, useState, type MouseEvent, type ReactNode } from 'react'
import { ChevronRightIcon } from '../components/Icons'
import { diffLines, type DiffLine } from './items'

/** Class names, those given and not false. */
export function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(' ')
}

/** A fold that would hide this many lines or fewer is not made. */
const FOLD_SLACK = 3

/**
 * Where a text folds: after `maxLines` lines or `maxChars` characters,
 * whichever comes first; -1 when it is no longer than that, or by a few
 * lines only.
 */
export function foldPoint(text: string, maxLines: number, maxChars: number): number {
  const byLength = text.length > maxChars ? maxChars : -1
  let at = -1
  for (let line = 0; line < maxLines; line++) {
    at = text.indexOf('\n', at + 1)
    if (at < 0) return byLength
    if (at >= maxChars) return maxChars
  }
  let ahead = at
  for (let extra = 0; extra < FOLD_SLACK; extra++) {
    if (ahead >= text.length - 1) return byLength
    ahead = text.indexOf('\n', ahead + 1)
    if (ahead < 0) return byLength
  }
  return ahead >= text.length - 1 ? byLength : at
}

function lineCount(text: string): number {
  let count = 1
  for (let at = text.indexOf('\n'); at >= 0 && at < text.length - 1; at = text.indexOf('\n', at + 1)) count += 1
  return count
}

/**
 * A text as written, folded past `lines` lines: an output of 32 KB puts a
 * few lines on the page until it is asked for, so a long one costs nothing
 * to scroll past. The text is its own (never markup) and can be selected.
 */
export function FoldedText({
  text,
  lines = 20,
  className,
  onFold,
}: {
  text: string
  lines?: number
  className?: string
  /** Opened or closed by hand. */
  onFold?: () => void
}) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const cut = useMemo(() => foldPoint(text, lines, lines * 240), [text, lines])
  const total = useMemo(() => (cut < 0 ? 0 : lineCount(text)), [text, cut])
  const folded = cut >= 0 && !open
  return (
    <div className="code-folded">
      <div id={id} className={cx('code-text', className, folded && 'is-folded')}>
        {folded ? text.slice(0, cut) : text}
      </div>
      {cut >= 0 && (
        <button
          type="button"
          className="button button-quiet button-small code-more"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => {
            onFold?.()
            setOpen((was) => !was)
          }}
        >
          {open ? 'Show less' : total > lines ? `Show all ${total} lines` : 'Show all'}
        </button>
      )}
    </div>
  )
}

/**
 * The head of a row that opens: a chevron, which is the button the keyboard
 * reaches, then what the row says. A click anywhere on the head opens it
 * too, unless it ends a selection: the head's text can be selected and
 * copied like any other.
 */
export function FoldHead({
  open,
  onToggle,
  controls,
  label,
  children,
}: {
  open: boolean
  onToggle: () => void
  /** The id of what it opens. */
  controls: string
  /** What the chevron's button is called: what the row is. */
  label: string
  children: ReactNode
}) {
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as Element).closest('button, a')) return
    if (window.getSelection()?.toString()) return
    onToggle()
  }
  return (
    <div className="code-fold-head" onClick={onClick}>
      <button type="button" className="code-fold-toggle" aria-expanded={open} aria-controls={open ? controls : undefined} aria-label={label} onClick={onToggle}>
        <ChevronRightIcon size={12} />
      </button>
      {children}
    </div>
  )
}

/** The lines of a diff a transcript shows at once; the rest wait behind "Show all". */
const DIFF_LINES = 400

/**
 * A unified diff, line by line: what it adds, what it removes, each hunk's
 * header. The file's own header lines are left out where the row above
 * names the file. Each line is text of its own, never markup.
 */
export function DiffView({ diff, header = false, onFold }: { diff: string; header?: boolean; onFold?: () => void }) {
  const lines = useMemo(() => {
    const all = diffLines(diff)
    return header ? all : all.filter((line) => line.kind !== 'meta')
  }, [diff, header])
  const [all, setAll] = useState(false)
  const shown: readonly DiffLine[] = all || lines.length <= DIFF_LINES ? lines : lines.slice(0, DIFF_LINES)
  if (lines.length === 0) return <p className="code-quiet">Nothing changed.</p>
  return (
    <div className="code-diff">
      <pre className="code-diff-lines">
        <code>
          {shown.map((line, i) => (
            <span key={i} className={`code-diff-line is-${line.kind}`}>
              {line.text === '' ? ' ' : line.text}
            </span>
          ))}
        </code>
      </pre>
      {shown.length < lines.length && (
        <button
          type="button"
          className="button button-quiet button-small code-more"
          onClick={() => {
            onFold?.()
            setAll(true)
          }}
        >
          Show all {lines.length} lines
        </button>
      )}
    </div>
  )
}
